import ClientDialogue from '../components/client/ClientDialogue';
import AssistantView from '../components/assistant/AssistantView';
import NavBar from '../components/base/NavBar';
import ChatList from '../components/chat/ChatList';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectLoggedIn, selectUserRole } from '../store/auth/authSlice';
import { useEffect, useState } from 'react';
import {
  getAssistantMessageScoresThunk,
  listActors,
  listChats,
} from '../store/chat/chatThunks';
import { refreshWebsocket } from '../store/chat/chatSlice';
import { selectAppMode, setChatMode } from '../store/settings/settingsSlice';
import LetsTalkView from '../components/assistant/LetsTalkView';
import AnimatePresence from '../components/base/AnimatePresence';
import { LayoutGroup, motion } from 'framer-motion';
import { generateGradients, getActorId, MOTION_KEYS } from '../util';
import Footer from '../components/base/Footer';
import { Button, colors, Divider, Tooltip } from '@nextui-org/react';
import { FaChevronRight } from 'react-icons/fa6';
import Icon from '../components/base/Icon';
import UserView from '../components/user/UserView';
import ClientProfile from '../components/client/ClientProfile';
import { setCurrentClient } from '../store/client/clientSlice';

export default function ChatPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectLoggedIn);
  const appMode = useAppSelector(selectAppMode);
  const [chatListShown, setChatListShown] = useState(true);
  const [assistantViewShown, setAssistantViewShown] = useState(true);
  const userRole = useAppSelector(selectUserRole);
  const [searchParams] = useSearchParams();
  const actor = searchParams.get('actor')?.toLowerCase();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [navigate, isLoggedIn]);

  useEffect(() => {
    if (actor) {
      dispatch(setChatMode('actors'));
      dispatch(setCurrentClient(getActorId(actor)));
    }
  }, [actor, dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(listChats());
      dispatch(listActors());
      dispatch(refreshWebsocket());
      dispatch(getAssistantMessageScoresThunk());
    }
  }, [dispatch, isLoggedIn]);

  const toggleChatList = () => setChatListShown((isShown) => !isShown);

  const toggleAssistantView = () =>
    setAssistantViewShown((isShown) => !isShown);

  return (
    <motion.div
      variants={{
        [MOTION_KEYS.INITIAL]: {},
        [MOTION_KEYS.ANIMATE]: {
          background: generateGradients([
            colors.red[50],
            colors.yellow[50],
            colors.green[50],
            colors.cyan[50],
            colors.blue[50],
            colors.purple[50],
          ]),
          transition: { duration: 60, repeat: Infinity, ease: 'linear' },
        },
        [MOTION_KEYS.EXIT]: {},
      }}
      initial={MOTION_KEYS.INITIAL}
      animate={MOTION_KEYS.ANIMATE}
      exit={MOTION_KEYS.EXIT}
      className="flex flex-col items-center w-screen h-screen"
    >
      <NavBar page={'chat'} />
      {/* height deducted for navbar and footer */}
      <div className="relative w-full h-[calc(100%-5.5rem-4rem)] min-h-0">
        <AnimatePresence mode="wait">
          {appMode === 'dialogue' && (
            <motion.div
              key="dialogue-group"
              variants={{
                [MOTION_KEYS.INITIAL]: { opacity: 0 },
                [MOTION_KEYS.ANIMATE]: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 },
                },
                [MOTION_KEYS.EXIT]: {
                  opacity: 0,
                  transition: { staggerChildren: 0.1 },
                },
              }}
              initial={MOTION_KEYS.INITIAL}
              animate={MOTION_KEYS.ANIMATE}
              exit={MOTION_KEYS.EXIT}
              className="relative flex flex-row w-full h-full min-h-0 p-2 overflow-hidden"
            >
              <LayoutGroup>
                <AnimatePresence>
                  {chatListShown && (
                    <motion.div
                      variants={{
                        [MOTION_KEYS.INITIAL]: { opacity: 0, x: '-100%' },
                        [MOTION_KEYS.ANIMATE]: { opacity: 1, x: '0%' },
                      }}
                      initial={MOTION_KEYS.INITIAL}
                      animate={MOTION_KEYS.ANIMATE}
                      exit={MOTION_KEYS.INITIAL}
                      className="relative flex flex-shrink-0 flex-row w-72 z-10"
                    >
                      <ChatList />
                    </motion.div>
                  )}
                </AnimatePresence>
                <motion.div
                  layout="preserve-aspect"
                  className={`relative flex flex-1 p-2 mx-auto ${
                    assistantViewShown ? 'max-w-none' : 'max-w-5xl'
                  } min-h-0`}
                >
                  <ClientDialogue />
                </motion.div>
                <AnimatePresence>
                  {assistantViewShown && userRole !== 'user_trainee' && (
                    <motion.div
                      layout="preserve-aspect"
                      variants={{
                        [MOTION_KEYS.INITIAL]: { opacity: 0, x: '100%' },
                        [MOTION_KEYS.ANIMATE]: { opacity: 1, x: '0%' },
                      }}
                      initial={MOTION_KEYS.INITIAL}
                      animate={MOTION_KEYS.ANIMATE}
                      exit={MOTION_KEYS.INITIAL}
                      className="relative flex flex-shrink-0 w-1/3 min-h-0 p-2"
                    >
                      <AssistantView />
                    </motion.div>
                  )}
                </AnimatePresence>
              </LayoutGroup>
            </motion.div>
          )}
          {appMode === 'letstalk' && (
            <motion.div
              key="letstalk-group"
              variants={{
                [MOTION_KEYS.INITIAL]: { opacity: 0 },
                [MOTION_KEYS.ANIMATE]: { opacity: 1 },
                [MOTION_KEYS.EXIT]: { opacity: 0 },
              }}
              initial={MOTION_KEYS.INITIAL}
              animate={MOTION_KEYS.ANIMATE}
              exit={MOTION_KEYS.EXIT}
              className={
                'absolute flex left-0 right-0 -top-4 mx-auto ' +
                'w-full h-full min-h-0 max-w-screen-lg max-h-full'
              }
            >
              <LetsTalkView />
            </motion.div>
          )}
          {appMode === 'user-management' && (
            <motion.div
              key="user-management-group"
              variants={{
                [MOTION_KEYS.INITIAL]: { opacity: 0 },
                [MOTION_KEYS.ANIMATE]: { opacity: 1 },
                [MOTION_KEYS.EXIT]: { opacity: 0 },
              }}
              initial={MOTION_KEYS.INITIAL}
              animate={MOTION_KEYS.ANIMATE}
              exit={MOTION_KEYS.EXIT}
              className={
                'relative flex flex-row w-full h-full p-4 overflow-hidden'
              }
            >
              <UserView />
            </motion.div>
          )}
          {appMode === 'client-profile' && (
            <motion.div
              key="client-profile-group"
              variants={{
                [MOTION_KEYS.INITIAL]: { opacity: 0 },
                [MOTION_KEYS.ANIMATE]: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 },
                },
                [MOTION_KEYS.EXIT]: {
                  opacity: 0,
                  transition: { staggerChildren: 0.1 },
                },
              }}
              initial={MOTION_KEYS.INITIAL}
              animate={MOTION_KEYS.ANIMATE}
              exit={MOTION_KEYS.EXIT}
              className="relative flex flex-row w-full h-full min-h-0 p-2 overflow-hidden"
            >
              <LayoutGroup>
                <AnimatePresence>
                  {chatListShown && (
                    <motion.div
                      variants={{
                        [MOTION_KEYS.INITIAL]: { opacity: 0, x: '-100%' },
                        [MOTION_KEYS.ANIMATE]: { opacity: 1, x: '0%' },
                      }}
                      initial={MOTION_KEYS.INITIAL}
                      animate={MOTION_KEYS.ANIMATE}
                      exit={MOTION_KEYS.INITIAL}
                      className="relative flex flex-shrink-0 flex-row w-72 z-10"
                    >
                      <ChatList />
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  <motion.div
                    layout="preserve-aspect"
                    className={`relative flex flex-1 p-2 mx-auto ${
                      assistantViewShown ? 'max-w-none' : 'max-w-5xl'
                    } min-h-0`}
                  >
                    <ClientProfile />
                  </motion.div>
                </AnimatePresence>

                <AnimatePresence>
                  {assistantViewShown && (
                    <motion.div
                      layout="preserve-aspect"
                      variants={{
                        [MOTION_KEYS.INITIAL]: { opacity: 0, x: '100%' },
                        [MOTION_KEYS.ANIMATE]: { opacity: 1, x: '0%' },
                      }}
                      initial={MOTION_KEYS.INITIAL}
                      animate={MOTION_KEYS.ANIMATE}
                      exit={MOTION_KEYS.INITIAL}
                      className="relative flex flex-shrink-0 w-1/3 min-h-0 p-2"
                    >
                      <AssistantView />
                    </motion.div>
                  )}
                </AnimatePresence>
              </LayoutGroup>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <Divider orientation="horizontal" />
      <div className="flex flex-1 flex-row items-center w-full bg-white">
        <Tooltip content={`${chatListShown ? 'Hide' : 'Show'} chat list`}>
          <motion.div
            animate={{ rotateY: chatListShown ? 180 : 0 }}
            className="mx-2"
          >
            <Button
              isIconOnly
              radius="full"
              variant="light"
              onPress={toggleChatList}
            >
              <Icon icon={FaChevronRight} />
            </Button>
          </motion.div>
        </Tooltip>
        <Footer />
        {userRole !== 'user_trainee' && (
          <Tooltip
            content={`${assistantViewShown ? 'Hide' : 'Show'} assistant`}
          >
            <motion.div
              animate={{ rotateY: assistantViewShown ? 0 : 180 }}
              className="mx-2"
            >
              <Button
                isIconOnly
                radius="full"
                variant="light"
                onPress={toggleAssistantView}
              >
                <Icon icon={FaChevronRight} />
              </Button>
            </motion.div>
          </Tooltip>
        )}
      </div>
    </motion.div>
  );
}
